import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell, Content } from '../../components/ui/AppShell';
import { ChevronLeft } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useLeagues, useMyMemberships, useUnreadByLeague } from '../../hooks/useQueries';
import {
  FOCUSED_LEAGUE_ID,
  WAITING_LEAGUE_IDENTITIES
} from '../../data/leagues';
import { upcomingCyclingLeagues } from '../../lib/cyclingSchedule';
import { CyclingPlayCard } from './CyclingPlayCard';
import { CyclingRaceRow } from './CyclingRaceRow';
import { WaitingRow } from './WaitingRow';
import { WorldCupCard } from './WorldCupCard';

export default function ChooseLeagueScreen() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: leagues } = useLeagues();
  const { data: memberships } = useMyMemberships(uid);
  const unreadByLeague = useUnreadByLeague(uid);

  const wc = leagues?.find((l) => l.leagueId === FOCUSED_LEAGUE_ID);
  const joinedWc = (memberships ?? []).some((m) => m.leagueId === FOCUSED_LEAGUE_ID);
  const joinedLeagueIds = useMemo(
    () => new Set((memberships ?? []).map((m) => m.leagueId)),
    [memberships]
  );
  const allCyclingLeagues = useMemo(
    () => (leagues ?? []).filter((l) => l.sport === 'CYCLING'),
    [leagues]
  );
  const upcomingCycling = useMemo(() => upcomingCyclingLeagues(allCyclingLeagues), [allCyclingLeagues]);
  const liveCyclingLeagues = upcomingCycling.filter((l) => l.currentRoundId);

  return (
    <AppShell>
      <Content width="wide">
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => {
              impact('light');
              navigate('/');
            }}
            className="tap -ml-1 flex items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">Home</span>
          </button>
        </div>

        <div className="pt-[14px] md:pt-5">
          <h1 className="text-[30px] font-extrabold tracking-[-0.03em] leading-none text-ink">Competitions</h1>
          <div className="mt-2 text-[13px] font-medium text-ink2">
            {upcomingCycling.length > 0
              ? 'Football and cycling — jump into what\u2019s live, the rest land automatically.'
              : 'One competition is live right now — the rest are between seasons.'}
          </div>
        </div>

        <div className="flex items-baseline justify-between pt-6">
          <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">In play</span>
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-accent-ink">
            <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />
            Live
          </span>
        </div>

        <div className="mt-3 space-y-2 md:grid md:grid-cols-2 md:gap-2.5 md:space-y-0">
          <WorldCupCard
            league={wc}
            joined={joinedWc}
            uid={uid}
            unread={unreadByLeague[FOCUSED_LEAGUE_ID] ?? 0}
            onPlay={() => {
              impact('medium');
              navigate(`/play/${FOCUSED_LEAGUE_ID}`);
            }}
          />
          {liveCyclingLeagues.map((league) => (
            <CyclingPlayCard
              key={league.leagueId}
              league={league}
              joined={joinedLeagueIds.has(league.leagueId)}
              uid={uid}
              unread={unreadByLeague[league.leagueId] ?? 0}
              onPlay={() => {
                impact('medium');
                navigate(`/play/${league.leagueId}`);
              }}
            />
          ))}
        </div>

        {upcomingCycling.length > 0 ? (
          <>
            <div className="pt-7">
              <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">Cycling</span>
            </div>
            <p className="mt-1 text-[13px] font-medium leading-[1.4] text-ink2">
              Grand tours and classics — call the rider duels when each race goes live.
            </p>
            <div className="mt-3 space-y-2 md:grid md:grid-cols-2 md:gap-2.5 md:space-y-0">
              {upcomingCycling.map((league) => (
                <CyclingRaceRow
                  key={league.leagueId}
                  league={league}
                  joined={joinedLeagueIds.has(league.leagueId)}
                  unread={unreadByLeague[league.leagueId] ?? 0}
                  onPlay={() => {
                    impact('medium');
                    navigate(`/play/${league.leagueId}`);
                  }}
                />
              ))}
            </div>
          </>
        ) : null}

        <div className="pt-7">
          <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">Between seasons</span>
        </div>
        <p className="mt-1 text-[13px] font-medium leading-[1.4] text-ink2">
          These return when their next season kicks off. We&apos;ll bring them back online automatically.
        </p>

        <div className="mt-3 space-y-2 md:grid md:grid-cols-2 md:gap-2.5 md:space-y-0">
          {WAITING_LEAGUE_IDENTITIES.map((l) => (
            <WaitingRow
              key={l.leagueId}
              identity={l}
              league={leagues?.find((d) => d.leagueId === l.leagueId)}
              unread={unreadByLeague[l.leagueId] ?? 0}
            />
          ))}
        </div>
      </Content>
    </AppShell>
  );
}
